import { useIdleCallback } from '@/common/hooks/useIdleCallback'
import { UseComboboxStateChange } from 'downshift'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchPerimeterProps } from '../circlePerimeter'
import { filterSearchItems } from '../filterSearchItems'
import { SearchItem } from '../searchTypes'

export function useSearch(
  items: SearchItem[],
  defaultEmpty: boolean,
  additionalTopItems?: SearchItem[],
  createItem?: boolean,
  // ORG-02: optional soft-partition perimeter, opt-in only for the circle
  // picker (CircleSearchButton/CircleSearchInput via CircleFormController).
  // When omitted, this hook behaves strictly as before (members/threads/
  // base-roles searches are unaffected).
  perimeter?: SearchPerimeterProps
) {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')

  // Filtered items using incremental search
  const filteredItems = useMemo(
    () =>
      filterSearchItems(items, searchText, {
        defaultEmpty,
        additionalTopItems,
        createLabel: createItem
          ? (name) => t(`useSearch.create`, { name }) as string
          : undefined,
        perimeter,
      }),
    [
      t,
      searchText,
      items,
      defaultEmpty,
      additionalTopItems,
      createItem,
      perimeter,
    ]
  )

  // Search when input value changes
  const onInputValueChange = useIdleCallback(
    ({ inputValue }: UseComboboxStateChange<SearchItem>) => {
      setSearchText(inputValue || '')
    },
    []
  )

  return {
    filteredItems,
    onInputValueChange,
  }
}
